/**
 * Primitive Metadata Configuration
 *
 * Transforms @unwritten/domain-registry data into UI-friendly format
 * Single source of truth: DOMAIN_CATALOG
 */
import { DOMAIN_CATALOG } from '@unwritten/domain-registry';
import { Brain, FileText, Layers, TrendingUp, Target, BookOpen, User, Users, Calendar, Globe, Gamepad2, MapPin, Building2, SlidersHorizontal, Heart, Battery, Flame, Coins, Eye, Radio, HelpCircle, EyeOff, Zap, } from 'lucide-react';
/**
 * Map domain icons to Lucide React components
 */
const ICON_MAP = {
    brain: Brain,
    'file-text': FileText,
    layers: Layers,
    'trending-up': TrendingUp,
    target: Target,
    'book-open': BookOpen,
    book: BookOpen,
    user: User,
    users: Users,
    'user-circle': User,
    calendar: Calendar,
    globe: Globe,
    'gamepad-2': Gamepad2,
    'map-pin': MapPin,
    'building-2': Building2,
    'sliders-horizontal': SlidersHorizontal,
    heart: Heart,
    battery: Battery,
    flame: Flame,
    coins: Coins,
    eye: Eye,
    'eye-off': EyeOff,
    radio: Radio,
    'help-circle': HelpCircle,
    infinity: Globe,
    zap: Zap,
    square: Layers,
};
/**
 * Database persistence status per primitive
 */
const HAS_DATABASE = {
    memory: true,
    template: true,
    card: false, // Runtime only, ephemeral
    beat: true,
    goal: true,
    canon: true,
    player: true,
    npc: true,
    character: true,
    season: true,
    world: true,
    game: true,
    universe: true,
    setting: true,
    society: true,
    modifier: true,
    bias: true,
    capacity: true,
    burnout: true,
    personality: true,
    resources: true,
};
/**
 * Generate UI tags from domain metadata
 */
function generateTags(domain) {
    const tags = [];
    if (domain.metadata?.status === 'implemented') {
        tags.push('Implemented');
    }
    else if (domain.metadata?.status === 'draft') {
        tags.push('Draft');
    }
    if (domain.metadata?.experimental) {
        tags.push('Experimental');
    }
    if (domain.category === 'narrative')
        tags.push('Narrative');
    if (domain.category === 'entity')
        tags.push('Entity');
    if (domain.category === 'world')
        tags.push('World Building');
    if (domain.category === 'systems')
        tags.push('Systems');
    if (domain.category === 'mechanics')
        tags.push('Mechanics');
    if (domain.relationships && domain.relationships.length > 0) {
        tags.push('Relationships');
    }
    if (domain.tables.length > 0) {
        tags.push(`${domain.tables.length} table${domain.tables.length !== 1 ? 's' : ''}`);
    }
    return tags;
}
/**
 * Map domain category to primitive category
 */
function mapDomainCategoryToPrimitiveCategory(category) {
    switch (category) {
        case 'narrative':
            return 'gameplay';
        case 'entity':
            return 'data';
        case 'world':
            return 'data';
        case 'systems':
            return 'gameplay';
        case 'mechanics':
            return 'gameplay';
        default:
            return 'data';
    }
}
/**
 * Transform domain to primitive metadata
 */
function transformDomainToPrimitive(domain) {
    const icon = ICON_MAP[domain.icon] || Brain;
    return {
        id: domain.id,
        name: domain.name,
        description: domain.description,
        category: mapDomainCategoryToPrimitiveCategory(domain.category),
        icon,
        color: domain.color,
        tags: generateTags(domain),
        servicePath: `@unwritten/game-primitives/${domain.id}`,
        hasDatabase: HAS_DATABASE[domain.id] ?? false,
    };
}
/**
 * Core primitive IDs (excludes sub-domains)
 */
const CORE_PRIMITIVE_IDS = [
    'memory',
    'template',
    'card',
    'beat',
    'goal',
    'canon',
    'player',
    'npc',
    'character',
    'season',
    'world',
    'game',
];
/**
 * Primitive metadata generated from domain catalog
 */
export const PRIMITIVE_METADATA = DOMAIN_CATALOG
    .filter((domain) => CORE_PRIMITIVE_IDS.includes(domain.id))
    .reduce((acc, domain) => {
    acc[domain.id] = transformDomainToPrimitive(domain);
    return acc;
}, {});
/**
 * Primitive categories for UI grouping
 */
export const PRIMITIVE_CATEGORIES = {
    data: {
        name: 'Data Primitives',
        color: '#3B82F6',
        primitives: ['memory', 'canon', 'player', 'npc', 'character', 'world'],
    },
    template: {
        name: 'Template & Config',
        color: '#8B5CF6',
        primitives: ['template', 'game'],
    },
    gameplay: {
        name: 'Gameplay',
        color: '#10B981',
        primitives: ['card', 'beat', 'goal', 'season'],
    },
};
